package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 앱 안 계정 삭제(Play 계정 삭제 요건, 2024-05-31 유예 종료 / Apple 심사 지침).
 *
 * 서버 계약은 `functions/src/accountDeletion.ts`에 있고 콜러블은 세 개다:
 * `edubridge-requestAccountDeletion`, `edubridge-cancelAccountDeletion`,
 * `edubridge-getAccountDeletionStatus`. 셋 다 로그인만 요구하며 학원 컨텍스트가 없다 —
 * 어느 학원에도 속하지 않은 계정도 똑같이 나갈 수 있어야 하기 때문이다.
 *
 * 세 콜러블 모두 [AccountDeletionState] 하나로 답한다. 화면이 필요로 하는 것은
 * "요청됐는가"가 아니라 "지금 무엇을 보여줘야 하는가"이고, 그 답에는 남은 유예와
 * 삭제를 막고 있는 학원 목록이 함께 필요하다.
 */
@Serializable
enum class AccountDeletionStatus {
    /** 요청 없음. */
    NONE,

    /** 유예 중이며 [AccountDeletionState.scheduledPurgeAt]에 파기된다. 취소할 수 있다. */
    SCHEDULED,

    /** 파기가 시작됐다. 되돌릴 수 없다. */
    PURGING,

    COMPLETED,
    CANCELLED,

    /** 파기 중 실패. 운영자가 재시도한다. */
    FAILED,
}

/** 삭제를 막는 사유. 둘 다 "이 계정이 사라지면 학원이 복구 불가능해진다"는 같은 문제다. */
@Serializable
enum class AccountDeletionBlockerReason {
    /** 학원장 본인. 학원을 먼저 넘겨야 한다. */
    ACADEMY_OWNER,

    /** 그 학원에 남은 마지막 ACTIVE 관리자. */
    LAST_ADMIN,
}

/** 어떤 학원이 왜 막고 있는지 — 화면이 "무엇을 먼저 정리해야 하는지" 말할 수 있게 한다. */
@Serializable
data class AccountDeletionBlocker(
    val academyId: String,
    val academyName: String,
    val reason: AccountDeletionBlockerReason,
)

/**
 * 삭제해도 남는 것. **문구가 아니라 키다** — 서버는 사실만 정하고 표현은 각 클라이언트가
 * 정한다. 이 목록을 화면에 반드시 보여줘야 한다: 이 시스템에서 "모두 즉시 완전 삭제"는
 * 사실이 아니고, 사실이 아닌 것을 말하는 것이 원래 고치려던 버그다.
 */
@Serializable
enum class AccountDeletionRetention {
    /** 학원의 수강생 대장·출결 이력은 학원 기록으로 남고 계정 연결만 끊긴다. */
    ACADEMY_RECORDS_UNLINKED,

    /** 게시글·댓글 본문은 남고 작성자 표시가 지워진다. */
    BOARD_CONTENT_ANONYMISED,

    /** 감사 기록은 보존된다. */
    AUDIT_LOG_RETAINED,
}

/**
 * 계정 삭제 화면의 전체 상태. 세 콜러블의 공통 응답이자 실패 표현이다.
 *
 * 실패를 예외가 아니라 값으로 담는 이유는 [CommandResult]와 같다 — Swift 경계를 넘는
 * uncaught Kotlin 예외는 복구 불가능한 종료다.
 */
@Serializable
data class AccountDeletionState(
    val status: AccountDeletionStatus = AccountDeletionStatus.NONE,
    val requestedAt: String? = null,

    /** ISO-8601. 이 시각 이후 첫 파기 배치에서 삭제된다. */
    val scheduledPurgeAt: String? = null,
    val cancelledAt: String? = null,
    val completedAt: String? = null,

    /** 유예 일수. 서버가 정하며 화면에 하드코딩하지 않는다. */
    val graceDays: Int = 0,
    val retentionNotice: List<AccountDeletionRetention> = emptyList(),

    /** 비어 있지 않으면 요청 자체가 거부된다. */
    val blockers: List<AccountDeletionBlocker> = emptyList(),
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null

    /** 유예 중 — 배너를 띄우고 취소 버튼을 보여줘야 하는 상태. */
    val isPending: Boolean get() = status == AccountDeletionStatus.SCHEDULED

    /** [AccountDeletionStatus.PURGING] 이후에는 되돌릴 것이 남아 있지 않다. */
    val canCancel: Boolean get() = status == AccountDeletionStatus.SCHEDULED

    /** 요청 버튼을 누르기 전에 미리 막아야 하는 상태. */
    val isBlocked: Boolean get() = blockers.isNotEmpty()
}
